import { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { useAuth0 } from '@auth0/auth0-react'
import { isInsecurelyValidJWT } from '../helpers/jwt'
import { RawCookieName } from '../constants'

const noop = (): void => undefined
const idTokenCookieName = RawCookieName.ID_TOKEN

export type IdToken = string | null

interface UseTokenResult {
  idToken: IdToken
}

function useIdToken(): UseTokenResult {
  const [cookies, setCookie] = useCookies([idTokenCookieName])
  const idTokenFromCookie = cookies[idTokenCookieName]
  const [idToken, setIdToken] = useState<IdToken>(
    isInsecurelyValidJWT(idTokenFromCookie) ? idTokenFromCookie : null
  )
  const { getAccessTokenSilently, getIdTokenClaims, isLoading } = useAuth0()

  useEffect(() => {
    if (isLoading) {
      return
    }

    if (!idToken || !isInsecurelyValidJWT(idToken)) {
      getAccessTokenSilently()
        .then(() => getIdTokenClaims())
        .then(claims => {
          setIdToken(claims.__raw)

          // Save the fresh ID token for future use
          return setCookie(idTokenCookieName, claims.__raw, { path: '/' })
        })
        .catch(noop)
    }
  }, [getAccessTokenSilently, getIdTokenClaims, isLoading, setCookie, idToken])

  if (idTokenFromCookie) {
    // If there is a token and is kind of valid, just use it.
    // The API will reject it if it is not actually valid.
    if (isInsecurelyValidJWT(idTokenFromCookie)) {
      return {
        idToken: idTokenFromCookie,
      }
    }
  }

  return {
    idToken,
  }
}

export default useIdToken
